import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { KeyRound, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [schoolCode, setSchoolCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: email.trim().toLowerCase(),
        school_code: schoolCode.trim().toUpperCase() || null,
      });
      setSubmitted(true);
      toast.success('Request sent');
    } catch (err) {
      toast.error('Could not submit request — please try again later');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border border-border" data-testid="forgot-password-page">
        <CardHeader>
          <div className="flex items-center gap-2">
            <KeyRound className="h-6 w-6 text-primary" />
            <CardTitle>Forgot password</CardTitle>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Submit your details and your school admin will issue a one-time password.
          </p>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="space-y-4 text-center">
              <p className="text-sm">
                If an account exists for <b>{email}</b>, your school admin has been notified
                and will share a temporary password shortly. You'll be asked to change it on
                your next login.
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full" data-testid="back-to-login-button">
                  Back to login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Registered email</Label>
                <Input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@school.com"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>School code</Label>
                <Input
                  value={schoolCode}
                  onChange={(e) => setSchoolCode(e.target.value)}
                  placeholder="e.g., BLDS26"
                  data-testid="forgot-school-code-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="submit-forgot-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Submit request
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Remembered it?{' '}
                <Link to="/login" className="text-primary hover:underline">
                  Back to login
                </Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
